/* eslint-disable @typescript-eslint/no-explicit-any */
import '@ibgroup-tws-webapps/ratio.ui/ratio.ui.css';
import './assets/module.pcss';
import {
  type ModuleConfig,
  type ModuleAPI,
  addEsmModule,
  makeModuleConfig,
  type ComponentConfig,
  type I18nModuleStorage,
} from '@ibgroup-tws-webapps/ratio.ui';
import { VueQueryPlugin } from '@tanstack/vue-query';
import { moduleRegistry } from '@ibgroup-tws-webapps/fluidity';
import { createPinia } from 'pinia';
import Tooltip from 'primevue/tooltip';
import AppShell from './components/AppShell.vue';
import MarketCard from './components/MarketCardInjectProvider.vue';
import ProductDetailsModule from './components/ProductDetailsModule.vue';
import ElectionHubModule from './electionHub/ElectionHubModule.vue';
import GdprConsentModule from './components/gdpr/GdprConsentModule.vue';
import MarketRulesCompare from './components/MarketRulesCompare.vue';
import MarketRulesDrawerModule from './components/MarketRulesDrawerModule.vue';
import { initFingerprintTracking } from './utils/fingerprintTracking';

const pinia = createPinia();

// Enable fingerprint tracking (ibmkttrack.js) for web platform users on load.
initFingerprintTracking();

async function mountWidgetModule(mountRoot: string | HTMLDivElement, config: ModuleConfig): Promise<ModuleAPI> {
  let moduleBuilder = addEsmModule(config.component, config.props)
    .withI18n(import.meta.glob('./../i18n/*.json') as I18nModuleStorage)
    .withStyles()
    .withAppId('forecast.trader2')
    .withDirectives({ tooltip: Tooltip })
    .withPlugin(pinia)
    .withPlugin(VueQueryPlugin);

  // Add router for AppShell (markets entry). landingRoutes nests the markets
  // grid and product-details as children of the persistent MarketsLayout parent,
  // so the category nav and its state persist across both views.
  if (config.component === AppShell) {
    const basePath = config.routerBasePath || '/';
    const { createRoutes: landingRoutes } = await import('./router/landingRoutes');
    moduleBuilder = moduleBuilder.withHashRouter(landingRoutes(basePath));
  }

  // Add router for ProductDetailsModule (deep-link entry). The host mounts this
  // module directly at the product-details base path — there is NO markets page
  // in this mount, so we register only the flat product-details tab routes.
  // ProductDetails renders its own persistent L1 nav here; an L1 click navigates
  // back to markets via host navigation. (Previously this also injected the
  // markets landing routes at '/', which made ProductDetails's own router-view
  // render the markets page *inside* the product-details view — the stacked-nav
  // bleed bug. Removed.)
  if (config.component === ProductDetailsModule) {
    const basePath = config.routerBasePath || '/';
    const { createRoutes: productDetailsRoutes } = await import('./router/productDetailsRoutes');
    moduleBuilder = moduleBuilder.withHashRouter(productDetailsRoutes(basePath));
  }

  // Add router for ElectionHub component
  if (config.component === ElectionHubModule) {
    const basePath = config.routerBasePath || '/';
    const { createRoutes: electionHubRoutes } = await import('./router/electionHubRoutes');
    moduleBuilder = moduleBuilder.withHashRouter(electionHubRoutes(basePath));
  }

  const moduleAPI = await moduleBuilder.mount(mountRoot);

  return {
    ...moduleAPI,
    destroy: () => {
      return moduleAPI.destroy();
    },
  } as any;
}

const moduleConfigs: ComponentConfig[] = [
  {
    component: AppShell,
    mountFunc: mountWidgetModule,
    alias: 'ModuleLanding',
  },
  {
    component: MarketCard,
    mountFunc: mountWidgetModule,
  },
  {
    component: ProductDetailsModule,
    mountFunc: mountWidgetModule,
  },
  {
    component: ElectionHubModule,
    mountFunc: mountWidgetModule,
  },
  {
    component: GdprConsentModule,
    mountFunc: mountWidgetModule,
  },
  {
    component: MarketRulesCompare,
    mountFunc: mountWidgetModule,
  },
  {
    component: MarketRulesDrawerModule,
    mountFunc: mountWidgetModule,
  },
];

const moduleConfig = makeModuleConfig(moduleConfigs);

moduleRegistry.registerModule('forecast.trader2', moduleConfig);

export default moduleConfig;
